import React, { useState, useEffect, useCallback } from 'react';
import { RenderingEditorProps, CastAgent, StudioLocation } from '../types';
import Typography from '../../../basic/Typography';
import { castApi } from '../../Cast/services/castApi';
import { newsEditorApi } from '../services/newsEditorApi';
import styles from '../NewsEditor.module.css';

const RenderingEditor: React.FC<RenderingEditorProps> = ({
  jobId,
  rendering,
  onUpdate,
  onRerun
}) => {
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  // Cast and studio selection state - only one cast member can be selected
  const [selectedCast, setSelectedCast] = useState<CastAgent | null>(rendering.selected_cast?.[0] || null);
  const [selectedStudio, setSelectedStudio] = useState<StudioLocation | null>(rendering.selected_studio || null);
  const [isRerunning, setIsRerunning] = useState(false);
  const [rerunStatus, setRerunStatus] = useState<string>('');
  const [rerunProgress, setRerunProgress] = useState<number>(0);
  const [enableOutpaint, setEnableOutpaint] = useState(false);

  // Available cast agents (will be loaded from cast API)
  const [availableCast, setAvailableCast] = useState<CastAgent[]>([]);
  const [isLoadingCast, setIsLoadingCast] = useState(true);

  // Available studio locations (mocked data for now)
  const availableStudios: StudioLocation[] = [
    {
      id: 'sf_fei',
      name: 'SF FEI Studio',
      description: 'San Francisco Financial District studio with modern tech aesthetic',
      preview_url: '/cast/data/agents/Saobao/assets/chrsheet/SAOBAO_05_LIGHTING_00003_.png'
    },
    {
      id: 'nyc_newsroom',
      name: 'NYC Newsroom',
      description: 'Classic New York newsroom with traditional broadcast feel',
      preview_url: '/cast/data/agents/Saobao/assets/chrsheet/SAOBAO_05_LIGHTING_00004_.png'
    },
    {
      id: 'la_modern',
      name: 'LA Modern Studio',
      description: 'Contemporary Los Angeles studio with sleek design',
      preview_url: '/cast/data/agents/Saobao/assets/chrsheet/SAOBAO_06_CHARACTER_SHEET_2-1_00001_.png'
    }
  ];

  // Get all available scenes dynamically
  const [availableScenes, setAvailableScenes] = useState<number[]>([]);
  const [isLoadingScenes, setIsLoadingScenes] = useState(true);

  // Load video URLs for different scenes
  const getVideoUrl = useCallback((sceneIndex: number) => {
    return `/news/out/${jobId}/render_stage/scene_${sceneIndex}/video.mp4`;
  }, [jobId]);

  const getThumbnailUrl = (sceneIndex: number) => {
    return `/news/out/${jobId}/render_stage/scene_${sceneIndex}/broll.png`;
  };

  const handleVideoLoad = () => {
    setIsLoadingVideo(false);
    setVideoError(null);
  };

  const handleVideoError = () => {
    setIsLoadingVideo(false);
    setVideoError('Failed to load video');
  };

  // Cast selection handlers
  // Cast selection handler - only one cast member can be selected
  const handleCastToggle = (agent: CastAgent) => {
    setSelectedCast(prev => {
      // If clicking the same agent, deselect it
      if (prev && prev.id === agent.id) {
        return null;
      }
      // Otherwise, select the new agent (replacing any previous selection)
      return agent;
    });
  };

  // Studio selection handler
  const handleStudioSelect = (studio: StudioLocation) => {
    setSelectedStudio(studio);
  };

    // Load cast agents from cast API
  const loadCastAgents = async () => {
    try {
      setIsLoadingCast(true);
      // Use the castApi service which handles the proper URLs
      const agents = await castApi.listAgents();

      // Transform cast API response to our CastAgent format
      const transformedAgents: CastAgent[] = agents.map((agent: any) => ({
        id: agent.id,
        name: agent.name,
        headshot_url: agent.headshot_url ? `/cast/${agent.headshot_url}` : null,
        description: agent.bio || 'No description available'
      }));

      setAvailableCast(transformedAgents);
    } catch (error) {
      console.error('Error loading cast agents:', error);
      // Fallback to default agents if API fails
      const fallbackAgents: CastAgent[] = [
        {
          id: 'saobao',
          name: 'Saobao',
          headshot_url: null,
          description: 'Dynamic news anchor with engaging personality'
        },
        {
          id: 'zaia',
          name: 'Zaia',
          headshot_url: null,
          description: 'Professional journalist with authoritative presence'
        },
        {
          id: 'kino',
          name: 'Kino',
          headshot_url: null,
          description: 'Charismatic host with modern appeal'
        }
      ];
      setAvailableCast(fallbackAgents);
    } finally {
      setIsLoadingCast(false);
    }
  };

  // Load cast agents when component mounts
  useEffect(() => {
    loadCastAgents();
  }, []);

  // Rerun handler
  const handleRerun = async () => {
    if (!selectedStudio || !selectedCast) {
      alert('Please select a cast member and a studio location');
      return;
    }

    setIsRerunning(true);
    setRerunStatus('Starting render rerun...');
    setRerunProgress(10);

    try {
      // Call the real news API to trigger rendering with cast selection and outpaint option
      await newsEditorApi.triggerRendering(jobId, selectedCast, selectedStudio, enableOutpaint);

      setRerunStatus('Rendering started successfully!');
      setRerunProgress(100);

      // Update the local state
      const updatedRendering = {
        ...rendering,
        selected_cast: selectedCast ? [selectedCast] : [],
        selected_studio: selectedStudio,
        status: 'processing' as const,
        last_updated: new Date().toISOString()
      };
      onUpdate(updatedRendering);

      // Note: We don't call onRerun here anymore to prevent duplicate render jobs
      // The real rendering is handled by the direct API call above

    } catch (error) {
      console.error('Error during render rerun:', error);
      setRerunStatus(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTimeout(() => {
        setIsRerunning(false);
        setRerunStatus('');
        setRerunProgress(0);
      }, 3000);
    }
  };

    // Check if video files exist
  const checkVideoExists = async (url: string) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Error checking video existence:', error);
      return false;
    }
  };

  // Discover all available scenes
  const discoverAvailableScenes = useCallback(async () => {
    setIsLoadingScenes(true);
    const scenes: number[] = [];

    // Check scenes 0-20 (reasonable range for news episodes)
    for (let i = 0; i <= 20; i++) {
      const videoUrl = getVideoUrl(i);
      const exists = await checkVideoExists(videoUrl);
      if (exists) {
        scenes.push(i);
        console.log(`Scene ${i} video exists`);
      }
    }

    console.log('Available scenes found:', scenes);
    setAvailableScenes(scenes);
    setIsLoadingScenes(false);
  }, [getVideoUrl]);

  // Discover scenes when component mounts or jobId changes
  useEffect(() => {
    discoverAvailableScenes();
  }, [jobId, discoverAvailableScenes]);

  return (
    <div className={styles['rendering-editor']}>
      <div className={styles['editor-header']}>
        <Typography variant="h4">Video Rendering</Typography>
        <div className={styles['editor-actions']}>
          <button className={styles['adjust-parameters-button']}>Adjust Parameters</button>
          <button className={styles['preview-button']}>Preview</button>
        </div>
      </div>

              {/* Cast and Studio Selection for Rerun */}
        <div className={styles['rerun-selection-section']}>
          <Typography variant="h5">Rerun Configuration</Typography>
          <Typography variant="caption" color="secondary" style={{ marginBottom: '1rem', display: 'block' }}>
            Cast agents are loaded from the Cast service. Make sure the service is running for full functionality. Only one cast member can be selected. Rerun will use the selected cast and studio to regenerate the video.
          </Typography>

        {/* Cast Selection */}
        <div className={styles['cast-selection']}>
          <Typography variant="h6">Select Cast Members</Typography>
          {isLoadingCast ? (
            <div className={styles['cast-loading']}>
              <Typography variant="body2" color="secondary">Loading cast agents...</Typography>
            </div>
          ) : availableCast.length === 0 ? (
            <div className={styles['cast-error']}>
              <Typography variant="body2" color="secondary">No cast agents available. Please check if the cast service is running.</Typography>
              <button
                className={styles['retry-button']}
                onClick={loadCastAgents}
                style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className={styles['cast-grid']}>
              {availableCast.map((agent) => (
                <div
                  key={agent.id}
                  className={`${styles['cast-agent']} ${selectedCast && selectedCast.id === agent.id ? styles['selected'] : ''}`}
                  onClick={() => handleCastToggle(agent)}
                >
                  {agent.headshot_url ? (
                    <img
                      src={agent.headshot_url}
                      alt={agent.name}
                      className={styles['cast-headshot']}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : (
                    <div className={styles['cast-headshot-placeholder']}>👤</div>
                  )}
                  <div className={styles['cast-info']}>
                    <Typography variant="body2" className={styles['cast-name']}>{agent.name}</Typography>
                    <Typography variant="caption" color="secondary" className={styles['cast-description']}>
                      {agent.description}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Studio Selection */}
        <div className={styles['studio-selection']}>
          <Typography variant="h6">Select Studio Location</Typography>
          <div className={styles['studio-grid']}>
            {availableStudios.map((studio) => (
              <div
                key={studio.id}
                className={`${styles['studio-option']} ${selectedStudio?.id === studio.id ? styles['selected'] : ''}`}
                onClick={() => handleStudioSelect(studio)}
              >
                {studio.preview_url && (
                  <img
                    src={studio.preview_url}
                    alt={studio.name}
                    className={styles['studio-preview']}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                )}
                <div className={styles['studio-preview-placeholder']}>No preview</div>
                <div className={styles['studio-info']}>
                  <Typography variant="body2" className={styles['studio-name']}>{studio.name}</Typography>
                  <Typography variant="caption" color="secondary" className={styles['studio-description']}>
                    {studio.description}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Outpaint Option */}
        <div className={styles['outpaint-option']}>
          <Typography variant="h6">Video Enhancement</Typography>
          <div className={styles['outpaint-toggle-container']}>
            <label className={styles['outpaint-toggle-label']}>
              <input
                type="checkbox"
                checked={enableOutpaint}
                onChange={(e) => setEnableOutpaint(e.target.checked)}
                className={styles['outpaint-toggle-input']}
              />
              <span className={styles['outpaint-toggle-slider']}>
                <span className={styles['outpaint-toggle-icon']}>🎬</span>
                <span className={styles['outpaint-toggle-icon']}>⚡</span>
              </span>
              <span className={styles['outpaint-toggle-text']}>
                Enable Video Outpaint
              </span>
            </label>
            <Typography variant="caption" color="secondary" className={styles['outpaint-description']}>
              {enableOutpaint
                ? "🎨 Enhanced rendering with 32px padding and improved framing (slower but higher quality)"
                : "⏭️ Standard rendering without outpaint processing (faster processing)"
              }
            </Typography>
          </div>
        </div>

        {/* Rerun Button and Status */}
        <div className={styles['rerun-controls']}>
          <button
            className={styles['rerun-button']}
            onClick={handleRerun}
            disabled={isRerunning || !selectedStudio || !selectedCast}
          >
            {isRerunning ? '🔄 Rerunning...' : '🔄 Rerun Rendering'}
          </button>

          {isRerunning && (
            <div className={styles['rerun-status']}>
              <div className={styles['rerun-progress']}>
                <div className={styles['rerun-progress-bar']}>
                  <div
                    className={styles['rerun-progress-fill']}
                    style={{ width: `${rerunProgress}%` }}
                  ></div>
                </div>
                <Typography variant="body2" className={styles['rerun-status-text']}>
                  {rerunStatus}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles['rendering-status']}>
        <div className={styles['status-info']}>
          <Typography variant="body2" className={styles['status-label']}>Status:</Typography>
          <span className={`${styles['status-value']} ${styles[`status-${rendering.status}`]}`}>
            {rendering.status}
          </span>
        </div>

        {/* Dynamic Scene Video Previews */}
        <div className={styles['video-previews']}>
          {isLoadingScenes ? (
            <div className={styles['loading-scenes']}>
              <Typography variant="body2" color="secondary">Discovering available scenes...</Typography>
            </div>
          ) : availableScenes.length === 0 ? (
            <div className={styles['no-scenes-message']}>
              <Typography variant="body2" color="secondary">No rendered scenes found for this job.</Typography>
            </div>
          ) : (
            availableScenes.map((sceneIndex) => (
              <div key={sceneIndex} className={styles['video-preview-section']}>
                <Typography variant="h5">Scene {sceneIndex}</Typography>
                <div className={styles['video-container']}>
                  {isLoadingVideo && <div className={styles['loading-spinner']}>Loading...</div>}
                  {videoError && <div className={styles['video-error']}>{videoError}</div>}
                  <video
                    src={getVideoUrl(sceneIndex)}
                    controls
                    className={styles['rendering-video']}
                    poster={getThumbnailUrl(sceneIndex)}
                    onLoadStart={() => setIsLoadingVideo(true)}
                    onLoadedData={handleVideoLoad}
                    onError={handleVideoError}
                    playsInline
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Dynamic Thumbnail Previews */}
        <div className={styles['thumbnail-previews']}>
          {isLoadingScenes ? (
            <div className={styles['loading-thumbnails']}>
              <Typography variant="body2" color="secondary">Loading thumbnails...</Typography>
            </div>
          ) : availableScenes.length === 0 ? (
            <div className={styles['no-thumbnails-message']}>
              <Typography variant="body2" color="secondary">No thumbnails available.</Typography>
            </div>
          ) : (
            availableScenes.map((sceneIndex) => (
              <div key={sceneIndex} className={styles['thumbnail-section']}>
                <Typography variant="h6">Scene {sceneIndex}</Typography>
                <img
                  src={getThumbnailUrl(sceneIndex)}
                  alt={`Scene ${sceneIndex} thumbnail`}
                  className={styles['rendering-thumbnail']}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <div className={styles['thumbnail-placeholder']}>No thumbnail available</div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles['rendering-details']}>
        {rendering.duration && (
          <Typography variant="caption" color="secondary">Duration: {rendering.duration} seconds</Typography>
        )}
        {rendering.error && (
          <Typography variant="caption" color="secondary" className={styles['error-message']}>Error: {rendering.error}</Typography>
        )}
        <Typography variant="caption" color="secondary">Last Updated: {new Date(rendering.last_updated).toLocaleString()}</Typography>
      </div>
    </div>
  );
};

export default RenderingEditor;
